// Type-only imports are erased at compile time; mediabunny itself is only
// loaded via the dynamic imports inside compressVideoToLimit and
// remuxVideoToMp4, so the encoder code stays out of the main bundle until a
// video actually needs processing.
import type {
  Conversion,
  ConversionAudioOptions,
  ConversionVideoOptions,
  Input,
  InputAudioTrack,
  InputVideoTrack,
} from 'mediabunny';

import {
  AAC_ENCODER_BITRATES_BPS,
  FALLBACK_AUDIO_BITRATE_BPS,
  REMUX_AVC_BITRATE_FACTOR,
  TARGET_AUDIO_BITRATE_BPS,
  VIDEO_RETRY_BITRATE_FACTOR,
} from './MediaCompression.constants';
import {
  computeTargetWidth,
  computeVideoBitrateBudget,
  pickLongestEdgeCap,
  replaceFileExtension,
} from './MediaCompression.functions';
import {
  eCompressionFailReason,
  tCompressMediaResult,
  tCompressToLimitRequest,
} from './MediaCompression.types';

type tMediabunnyModule = typeof import('mediabunny');

type tAudioPlan = {
  options: ConversionAudioOptions | undefined;
  bitrateForBudget: number;
};

/**
 * WhatsApp requires MP4 with AAC audio. Re-encode audio to AAC when the
 * browser can (shrinks bloated stereo tracks); when it can't (e.g. Firefox),
 * pass an already-AAC source track through and budget its real bitrate.
 * Anything else cannot become a WhatsApp-valid MP4 → null.
 */
async function resolveAudioPlan(
  mediabunny: tMediabunnyModule,
  input: Input,
): Promise<tAudioPlan | null> {
  const audioTrack = await input.getPrimaryAudioTrack();
  if (!audioTrack) {
    return { options: undefined, bitrateForBudget: 0 };
  }

  const canEncodeAac = await mediabunny.canEncodeAudio('aac', {
    bitrate: TARGET_AUDIO_BITRATE_BPS,
  });
  if (canEncodeAac) {
    return {
      options: { codec: 'aac', bitrate: TARGET_AUDIO_BITRATE_BPS },
      bitrateForBudget: TARGET_AUDIO_BITRATE_BPS,
    };
  }

  const sourceCodec = await audioTrack.getCodec();
  if (sourceCodec !== 'aac') return null;

  let bitrateForBudget = FALLBACK_AUDIO_BITRATE_BPS;
  try {
    const stats = await audioTrack.computePacketStats(200);
    if (stats.averageBitrate > 0) {
      bitrateForBudget = stats.averageBitrate;
    }
  } catch {
    // keep the conservative fallback estimate
  }

  return { options: undefined, bitrateForBudget };
}

/**
 * A discarded track does not invalidate a mediabunny conversion, so a track
 * this browser cannot decode or encode (Opus without an AAC encoder, primed
 * AAC that must be re-encoded to be trimmed) would silently ship a mute or
 * blank video. The primary picture and, when the source has one, the primary
 * sound must survive.
 */
function keepsPrimaryStreams(
  conversion: Conversion,
  hasAudio: boolean,
): boolean {
  const types = conversion.utilizedTracks.map((track) => track.type);
  return types.includes('video') && (!hasAudio || types.includes('audio'));
}

/**
 * Transcodes a video to an H.264/AAC MP4 that fits within maxBytes, using
 * WebCodecs (hardware-accelerated) via mediabunny. The video bitrate is
 * derived from the file's duration so the output lands just under the limit;
 * frames are downscaled only when the budget is too low for the source size.
 */
async function compressVideoToLimit(
  request: tCompressToLimitRequest,
): Promise<tCompressMediaResult> {
  const { file, maxBytes, onProgress } = request;

  if (
    typeof (globalThis as { VideoEncoder?: unknown }).VideoEncoder ===
    'undefined'
  ) {
    return { ok: false, reason: eCompressionFailReason.ENCODER_UNAVAILABLE };
  }

  // Surface progress immediately so the UI can show a determinate bar during
  // the chunk download + probe, before mediabunny emits its first real tick.
  onProgress?.(0);

  try {
    const mediabunny = await import('mediabunny');

    const probeInput = new mediabunny.Input({
      source: new mediabunny.BlobSource(file),
      formats: mediabunny.ALL_FORMATS,
    });

    const durationSeconds = await probeInput.computeDuration();
    const videoTrack = await probeInput.getPrimaryVideoTrack();
    if (!videoTrack || durationSeconds <= 0) {
      return { ok: false, reason: eCompressionFailReason.PROCESSING_ERROR };
    }

    const hasAudio = (await probeInput.getPrimaryAudioTrack()) !== null;
    const audioPlan = await resolveAudioPlan(mediabunny, probeInput);
    if (!audioPlan) {
      return { ok: false, reason: eCompressionFailReason.ENCODER_UNAVAILABLE };
    }

    const videoBitrate = computeVideoBitrateBudget({
      maxBytes,
      durationSeconds,
      audioBitrate: audioPlan.bitrateForBudget,
    });
    if (videoBitrate === null) {
      return { ok: false, reason: eCompressionFailReason.VIDEO_TOO_LONG };
    }

    const targetWidth = computeTargetWidth({
      width: videoTrack.displayWidth,
      height: videoTrack.displayHeight,
      longestEdgeCap: pickLongestEdgeCap(videoBitrate),
    });

    const canEncodeAvc = await mediabunny.canEncodeVideo('avc', {
      bitrate: videoBitrate,
    });
    if (!canEncodeAvc) {
      return { ok: false, reason: eCompressionFailReason.ENCODER_UNAVAILABLE };
    }

    const attemptBitrates = [
      videoBitrate,
      Math.floor(videoBitrate * VIDEO_RETRY_BITRATE_FACTOR),
    ];

    for (const attemptBitrate of attemptBitrates) {
      const input = new mediabunny.Input({
        source: new mediabunny.BlobSource(file),
        formats: mediabunny.ALL_FORMATS,
      });
      const output = new mediabunny.Output({
        format: new mediabunny.Mp4OutputFormat({ fastStart: 'in-memory' }),
        target: new mediabunny.BufferTarget(),
      });

      const conversion = await mediabunny.Conversion.init({
        input,
        output,
        tracks: 'primary',
        video: {
          codec: 'avc',
          bitrate: attemptBitrate,
          forceTranscode: true,
          ...(targetWidth !== null ? { width: targetWidth } : {}),
        },
        audio: audioPlan.options,
        showWarnings: false,
      });

      if (!conversion.isValid || !keepsPrimaryStreams(conversion, hasAudio)) {
        return {
          ok: false,
          reason: eCompressionFailReason.ENCODER_UNAVAILABLE,
        };
      }

      if (onProgress) {
        conversion.onProgress = (progress: number) => onProgress(progress);
      }

      await conversion.execute();

      const buffer = output.target.buffer;
      if (buffer && buffer.byteLength > 0 && buffer.byteLength <= maxBytes) {
        const compressedFile = new File(
          [buffer],
          replaceFileExtension(file.name, '.mp4'),
          { type: 'video/mp4' },
        );

        return {
          ok: true,
          file: compressedFile,
          wasCompressed: true,
          originalBytes: file.size,
          finalBytes: compressedFile.size,
        };
      }
    }

    return { ok: false, reason: eCompressionFailReason.OUTPUT_TOO_LARGE };
  } catch {
    return { ok: false, reason: eCompressionFailReason.PROCESSING_ERROR };
  }
}

async function measureBitrate(
  track: InputVideoTrack | InputAudioTrack,
): Promise<number | null> {
  try {
    const declared = await track.getAverageBitrate();
    if (declared && declared > 0) return declared;
    const stats = await track.computePacketStats(200);
    return stats.averageBitrate > 0 ? stats.averageBitrate : null;
  } catch {
    return null;
  }
}

type tRemuxAudioPlan = {
  options: ConversionAudioOptions;
  bitrateForBudget: number;
};

/**
 * Copies the audio when mediabunny can, and otherwise re-encodes it at the
 * source's bitrate rather than mediabunny's 192 kbps default. Mediabunny
 * re-encodes AAC whose first packet starts before the file's start time — the
 * encoder-delay ("priming") edit list that ffmpeg and phones write by default
 * — to trim that pre-roll. Passing a bitrate always forces a re-encode, so it
 * is passed only when one is going to happen anyway (BUG-880, option A).
 */
async function planRemuxAudio(
  audioTrack: InputAudioTrack,
  startTimestamp: number,
): Promise<tRemuxAudioPlan> {
  const sourceBitrate = await measureBitrate(audioTrack);
  const bitrateForBudget = sourceBitrate ?? FALLBACK_AUDIO_BITRATE_BPS;

  const isAac = (await audioTrack.getCodec()) === 'aac';
  const isPrimed = (await audioTrack.getFirstTimestamp()) < startTimestamp;
  if (isAac && !isPrimed) {
    return { options: { codec: 'aac' }, bitrateForBudget };
  }

  const bitrate =
    AAC_ENCODER_BITRATES_BPS.find((rate) => rate >= bitrateForBudget) ??
    AAC_ENCODER_BITRATES_BPS[AAC_ENCODER_BITRATES_BPS.length - 1];
  return { options: { codec: 'aac', bitrate }, bitrateForBudget: bitrate };
}

/**
 * H.264 is copied untouched. HEVC, VP9 and the rest must be transcoded, and
 * left to mediabunny's default quality a 4K clip comes out over the limit and
 * gets encoded a second time by compressVideoToLimit. The bitrate is capped by
 * what fits maxBytes and by twice the source's (H.264 needs roughly double
 * HEVC's for the same picture), so a short clip is not inflated to the limit.
 * Null when the file is too long to fit at a watchable bitrate.
 */
async function planRemuxVideo(
  videoTrack: InputVideoTrack,
  budget: { maxBytes: number; durationSeconds: number; audioBitrate: number },
): Promise<ConversionVideoOptions | null> {
  if ((await videoTrack.getCodec()) === 'avc') {
    return { codec: 'avc' };
  }

  const budgetBitrate = computeVideoBitrateBudget(budget);
  if (budgetBitrate === null) return null;

  const sourceBitrate = await measureBitrate(videoTrack);
  const bitrate =
    sourceBitrate === null
      ? budgetBitrate
      : Math.min(budgetBitrate, sourceBitrate * REMUX_AVC_BITRATE_FACTOR);
  return { codec: 'avc', bitrate: Math.floor(bitrate) };
}

/**
 * Rewrites a video into a fresh H.264/AAC MP4 container. H.264 is copied
 * packet for packet (no quality loss, no encoder needed); audio is copied
 * when mediabunny can, otherwise re-encoded at the source's bitrate; HEVC,
 * VP9 or Opus are transcoded. Only the primary video and audio tracks are
 * kept: Meta takes a single audio stream. This repairs MP4s whose container
 * Meta cannot read (131053 "No video stream found") and turns MOV/WebM into
 * the MP4 the WhatsApp Cloud API accepts. An output above maxBytes is
 * OUTPUT_TOO_LARGE and the caller falls back to compressVideoToLimit.
 */
async function remuxVideoToMp4(
  request: tCompressToLimitRequest,
): Promise<tCompressMediaResult> {
  const { file, maxBytes, onProgress } = request;

  onProgress?.(0);

  try {
    const mediabunny = await import('mediabunny');

    const input = new mediabunny.Input({
      source: new mediabunny.BlobSource(file),
      formats: mediabunny.ALL_FORMATS,
    });

    const videoTrack = await input.getPrimaryVideoTrack();
    if (!videoTrack) {
      return { ok: false, reason: eCompressionFailReason.NO_VIDEO_TRACK };
    }
    const audioTrack = await input.getPrimaryAudioTrack();

    // Mirrors how mediabunny picks the output's start time from the tracks
    // it converts (never below 0).
    const primaryTracks = audioTrack ? [videoTrack, audioTrack] : [videoTrack];
    const startTimestamp = Math.max(
      await input.getFirstTimestamp(primaryTracks),
      0,
    );

    const audioPlan = audioTrack
      ? await planRemuxAudio(audioTrack, startTimestamp)
      : null;

    const videoOptions = await planRemuxVideo(videoTrack, {
      maxBytes,
      durationSeconds: await input.computeDuration(),
      audioBitrate: audioPlan?.bitrateForBudget ?? 0,
    });
    if (!videoOptions) {
      return { ok: false, reason: eCompressionFailReason.VIDEO_TOO_LONG };
    }

    const output = new mediabunny.Output({
      format: new mediabunny.Mp4OutputFormat({ fastStart: 'in-memory' }),
      target: new mediabunny.BufferTarget(),
    });

    const conversion = await mediabunny.Conversion.init({
      input,
      output,
      tracks: 'primary',
      video: videoOptions,
      audio: audioPlan?.options ?? { codec: 'aac' },
      showWarnings: false,
    });

    if (!conversion.isValid || !keepsPrimaryStreams(conversion, !!audioTrack)) {
      return { ok: false, reason: eCompressionFailReason.ENCODER_UNAVAILABLE };
    }

    if (onProgress) {
      conversion.onProgress = (progress: number) => onProgress(progress);
    }

    await conversion.execute();

    const buffer = output.target.buffer;
    if (!buffer || buffer.byteLength === 0) {
      return { ok: false, reason: eCompressionFailReason.PROCESSING_ERROR };
    }
    if (buffer.byteLength > maxBytes) {
      return { ok: false, reason: eCompressionFailReason.OUTPUT_TOO_LARGE };
    }

    const remuxedFile = new File(
      [buffer],
      replaceFileExtension(file.name, '.mp4'),
      { type: 'video/mp4' },
    );

    return {
      ok: true,
      file: remuxedFile,
      wasCompressed: true,
      originalBytes: file.size,
      finalBytes: remuxedFile.size,
    };
  } catch {
    return { ok: false, reason: eCompressionFailReason.PROCESSING_ERROR };
  }
}

export const VideoCompressionService = Object.freeze({
  compressVideoToLimit,
  remuxVideoToMp4,
});
